import React, { createContext, useContext, useCallback, useEffect, useMemo } from 'react';
import { useReactive } from 'ahooks';
import { message } from 'antd';
import { useEditorContext } from '../../contexts/EditorContext';
import { useOutline } from '../../hooks/useOutline';
import { useRewriteTask } from '../../hooks/useRewriteTask';
import { extractBlockContentByBlockId } from '../../utils/blockConverter';
import { scrollEditorToPos } from '../../utils/editorScroll';
import { post } from '../../../../axios/axios';
import { PROCESSING_MODE, ACTION_TYPE, LENGTH_OPTION, TONE_OPTION, PANEL_MODE } from '../constants';

// 处理方式描述文案映射
export const modeDescriptions = {
    [PROCESSING_MODE.POLISH]: '优化文字表达，提升语言质量和流畅度。',
    [PROCESSING_MODE.SHRINK]: '缩短篇幅：在保留核心观点的同时，去除冗余修饰，使内容更精炼。',
    [PROCESSING_MODE.EXPAND]: '扩充内容：增加细节、案例和论述，使内容更加充分完整。',
    [PROCESSING_MODE.REDUCE_DUPLICATE]: '降低重复率：改写表达方式，保持原意的同时降低查重率。',
};

const RightPanelContext = createContext(null);

export const useRightPanelContext = () => {
    const context = useContext(RightPanelContext);
    if (!context) {
        throw new Error('useRightPanelContext must be used within RightPanelProvider');
    }
    return context;
};

export const RightPanelProvider = ({ children, onApplyTextResult }) => {
    const {
        editor,
        uiState,
        selectionState,
        documentState,
        scrollContainerRef,
        generating: contextGenerating,
        updateGenerationState,
        resetGenerationState,
        startChapterRewrite,
        updateChapterRewriteState,
        finishChapterRewrite,
        registerRewriteCanceller,
    } = useEditorContext();

    const outline = useOutline(editor);

    // 章节改稿任务（提交 → 轮询 → 结果 → 确认）
    const rewriteTask = useRewriteTask();

    // ========== 状态定义 ==========

    const commonState = useReactive({
        mode: PANEL_MODE.EMPTY,
        generating: false,
        generationProgress: 0,
        generationStatus: '',
        isConfirming: false,
        showPreview: false,
        previewMode: '',
        currentChapterInfo: null, // { node, pos, detailId, level, blockId }
        isResetting: false,
    });

    const textState = useReactive({
        selectedText: '',
        selectedRange: { from: null, to: null },
        selectedBlockIds: [],
        customInstruction: '',
        currentAction: '',
        lengthOption: LENGTH_OPTION.NORMAL,
        toneOption: TONE_OPTION.ACADEMIC,
        processingMode: PROCESSING_MODE.POLISH,
        modeDescription: modeDescriptions[PROCESSING_MODE.POLISH],
        previewResult: '',
    });

    // 章节改稿表单
    const chapterForm = useReactive({
        instruction: '',
        preset: null,
        useCnReference: true,
        useEnReference: true,
        referenceChapterIds: [],
    });

    const tableState = useReactive({
        isTableSelected: false,
        previewTableBlock: null,
        previewOriginalTableContent: '',
    });

    const imgState = useReactive({
        isImageSelected: false,
        previewImageBlock: null,
        previewOriginalImageUrl: '',
    });

    const formulaState = useReactive({
        isFormulaSelected: false,
        formulaInfo: null,
        previewFormulaText: '',
        previewOriginalFormulaText: '',
    });

    const chapterState = useReactive({
        previewChapterContent: null, // 改稿结果（tiptap JSON）
        previewOriginalContent: null, // 原文（节点数组）
        previewChapterRange: null,
    });

    // ========== 同步到 EditorContext ==========

    useEffect(() => {
        updateGenerationState({
            generating: commonState.generating,
            progress: commonState.generationProgress,
            status: commonState.generationStatus,
        });
    }, [
        commonState.generating,
        commonState.generationProgress,
        commonState.generationStatus,
        updateGenerationState,
    ]);

    useEffect(() => {
        if (!contextGenerating && commonState.generating) {
            commonState.generating = false;
            commonState.generationProgress = 0;
            commonState.generationStatus = '';
        }
    }, [contextGenerating, commonState]);

    // 镜像面板模式，供顶栏 / 移动端提示使用
    useEffect(() => {
        uiState.panelMode = commonState.mode;
    }, [commonState.mode, uiState]);

    // 遮罩层的「中断」按钮 → 取消任务
    useEffect(() => {
        registerRewriteCanceller(() => {
            rewriteTask.cancel();
            chapterState.previewChapterContent = null;
            chapterState.previewOriginalContent = null;
            chapterState.previewChapterRange = null;
        });
        return () => registerRewriteCanceller(null);
    }, [registerRewriteCanceller, rewriteTask.cancel, chapterState]);

    // ========== 章节树（参考其他章节 / 选择改稿对象） ==========

    const chapterTreeData = useMemo(() => {
        if (!outline || outline.length === 0) return [];
        const currentBlockId = commonState.currentChapterInfo?.node?.attrs?.blockId;
        const tree = [];
        const stack = [];

        outline.forEach((item) => {
            if (item.blockId === currentBlockId) return;
            const node = {
                label: item.text,
                value: item.blockId || item.id,
                title: item.text,
                level: item.level,
            };
            while (stack.length > 0 && stack[stack.length - 1].level >= item.level) {
                stack.pop();
            }
            if (stack.length === 0) {
                tree.push(node);
            } else {
                const parent = stack[stack.length - 1];
                if (!parent.children) parent.children = [];
                parent.children.push(node);
            }
            stack.push(node);
        });

        const markLeafNodes = (nodes) => {
            nodes.forEach((node) => {
                const isLeaf = !node.children || node.children.length === 0;
                node.disabled = !isLeaf;
                node.selectable = isLeaf;
                if (!isLeaf) markLeafNodes(node.children);
            });
        };
        markLeafNodes(tree);
        return tree;
    }, [outline, commonState.currentChapterInfo]);

    /** 可改稿的小节列表（供改稿面板的下拉选择） */
    const rewritableChapters = useMemo(
        () => outline.filter((item) => item.rewritable),
        [outline],
    );

    // ========== 工具函数 ==========

    const calculateTargetLength = useCallback((originalLength, processingMode, lengthOption) => {
        if (lengthOption === LENGTH_OPTION.ORIGINAL) return originalLength;
        if (processingMode === PROCESSING_MODE.SHRINK) {
            const ratios = {
                [LENGTH_OPTION.SHRINK]: 0.3,
                [LENGTH_OPTION.NORMAL]: 0.5,
                [LENGTH_OPTION.EXPAND]: 0.7,
            };
            const result = Math.round(originalLength * (ratios[lengthOption] || 0.5));
            return Math.max(1, Math.min(result, originalLength));
        }
        const ratios = {
            [LENGTH_OPTION.SHRINK]: 0.5,
            [LENGTH_OPTION.NORMAL]: 0.7,
            [LENGTH_OPTION.EXPAND]: 1.5,
        };
        return Math.max(1, Math.round(originalLength * (ratios[lengthOption] || 0.7)));
    }, []);

    const formatLengthDisplay = useCallback((targetLength, lengthOption, originalLength) => {
        if (lengthOption === LENGTH_OPTION.ORIGINAL) return '保持原长';
        if (originalLength === 0) {
            if (lengthOption === LENGTH_OPTION.SHRINK) return '~500字';
            if (lengthOption === LENGTH_OPTION.NORMAL) return '~700字';
            return '1000字+';
        }
        if (lengthOption === LENGTH_OPTION.EXPAND && targetLength >= 1000) {
            return `${targetLength}字+`;
        }
        return `~${targetLength}字`;
    }, []);

    // ========== 节点查找 ==========

    const findNodeByBlockId = useCallback((editorInstance, blockId, typeNames) => {
        if (!editorInstance || !blockId) return null;
        let result = null;
        editorInstance.state.doc.descendants((node, pos) => {
            if (result) return false;
            if (typeNames.includes(node.type.name) && node.attrs.blockId === blockId) {
                result = { node, pos };
                return false;
            }
            return true;
        });
        return result;
    }, []);

    const findImageNodeByBlockId = useCallback(
        (editorInstance, blockId) => findNodeByBlockId(editorInstance, blockId, ['image']),
        [findNodeByBlockId],
    );

    const findTableNodeByBlockId = useCallback(
        (editorInstance, blockId) => findNodeByBlockId(editorInstance, blockId, ['table']),
        [findNodeByBlockId],
    );

    const findFormulaNodeByBlockId = useCallback(
        (editorInstance, blockId) =>
            findNodeByBlockId(editorInstance, blockId, ['inlineMath', 'blockMath']),
        [findNodeByBlockId],
    );

    const findHeadingNodeByBlockId = useCallback(
        (editorInstance, blockId) => findNodeByBlockId(editorInstance, blockId, ['heading']),
        [findNodeByBlockId],
    );

    /** 章节范围：从标题开始，到下一个同级或更高级标题之前 */
    const findChapterRange = useCallback((editorInstance, chapterHeading) => {
        if (!editorInstance || !chapterHeading) return null;
        const { node: headingNode, pos: headingPos, level } = chapterHeading;
        const doc = editorInstance.state.doc;
        const headingEnd = headingPos + headingNode.nodeSize;

        let chapterEnd = doc.content.size;
        let sameLevelPos = null;
        let higherLevelPos = null;
        doc.nodesBetween(headingEnd, doc.content.size, (node, pos) => {
            if (node.type.name === 'heading') {
                if (node.attrs.level === level) {
                    if (sameLevelPos === null) sameLevelPos = pos;
                } else if (node.attrs.level < level) {
                    if (higherLevelPos === null) higherLevelPos = pos;
                }
            }
            return true;
        });

        if (sameLevelPos !== null) chapterEnd = sameLevelPos;
        else if (higherLevelPos !== null) chapterEnd = higherLevelPos;

        return {
            from: headingPos,
            to: chapterEnd,
            headingNode,
            headingPos,
            level,
            foundNextHeading: sameLevelPos !== null || higherLevelPos !== null,
        };
    }, []);

    const scrollToChapter = useCallback(
        (editorInstance, chapterHeading) => {
            if (!editorInstance || !chapterHeading) return;
            scrollEditorToPos(editorInstance, chapterHeading.pos, {
                container: scrollContainerRef.current,
                offset: 32,
            });
        },
        [scrollContainerRef],
    );

    // ========== 保存块内容 ==========

    const saveBlockContent = useCallback(
        (blockId, delay = 100) => {
            if (!editor || !blockId) return;
            setTimeout(async () => {
                try {
                    const editedContent = extractBlockContentByBlockId(editor, blockId);
                    if (!editedContent) return;
                    await post('/ai/ai/paper/editor/v2/save', {
                        contentBlockId: blockId,
                        editedContent,
                    });
                } catch (error) {
                    console.error('保存失败:', error);
                }
            }, delay);
        },
        [editor],
    );

    // ========== 预览 / 结果 ==========

    const handleCancelPreview = useCallback(() => {
        commonState.showPreview = false;
        commonState.previewMode = '';
        textState.previewResult = '';
        imgState.previewImageBlock = null;
        imgState.previewOriginalImageUrl = '';
        tableState.previewTableBlock = null;
        tableState.previewOriginalTableContent = '';
        formulaState.previewFormulaText = '';
        formulaState.previewOriginalFormulaText = '';
    }, [formulaState, imgState, tableState, textState, commonState]);

    const copyToClipboard = useCallback((text, successText = '已复制到剪贴板') => {
        if (!text) return;
        navigator.clipboard
            .writeText(text)
            .then(() => message.success(successText))
            .catch(() => message.error('复制失败，请重试'));
    }, []);

    const handleCopyResult = useCallback(() => {
        if (textState.previewResult) copyToClipboard(textState.previewResult);
        else if (formulaState.previewFormulaText)
            copyToClipboard(formulaState.previewFormulaText, '公式已复制到剪贴板');
    }, [textState, formulaState, copyToClipboard]);

    const handleReplaceContent = useCallback(() => {
        if (!textState.previewResult || !onApplyTextResult) return;
        const contentBlockId =
            textState.selectedBlockIds.length > 0 ? textState.selectedBlockIds[0] : null;
        onApplyTextResult(
            textState.previewResult,
            PANEL_MODE.SELECTION,
            textState.selectedRange,
            contentBlockId,
        );
        handleCancelPreview();
        commonState.mode = PANEL_MODE.EMPTY;
        textState.selectedText = '';
        textState.selectedBlockIds = [];
        textState.customInstruction = '';
        textState.currentAction = '';
        textState.selectedRange = { from: null, to: null };
    }, [commonState, textState, onApplyTextResult, handleCancelPreview]);

    // ========== 取消 / 重置 ==========

    const handleReset = useCallback(() => {
        commonState.isResetting = true;
        rewriteTask.reset();

        if (editor) {
            editor.commands.blur();
            const { from } = editor.state.selection;
            editor.commands.setTextSelection(from);
        }

        commonState.mode = PANEL_MODE.EMPTY;
        commonState.currentChapterInfo = null;
        commonState.generating = false;
        commonState.generationProgress = 0;
        commonState.generationStatus = '';
        commonState.showPreview = false;
        commonState.previewMode = '';

        textState.selectedText = '';
        textState.selectedBlockIds = [];
        textState.customInstruction = '';
        textState.currentAction = '';
        textState.lengthOption = LENGTH_OPTION.NORMAL;
        textState.toneOption = TONE_OPTION.ACADEMIC;
        textState.processingMode = PROCESSING_MODE.POLISH;
        textState.modeDescription = modeDescriptions[PROCESSING_MODE.POLISH];
        textState.previewResult = '';
        textState.selectedRange = { from: null, to: null };

        chapterForm.instruction = '';
        chapterForm.preset = null;
        chapterForm.useCnReference = true;
        chapterForm.useEnReference = true;
        chapterForm.referenceChapterIds = [];

        imgState.isImageSelected = false;
        imgState.previewImageBlock = null;
        imgState.previewOriginalImageUrl = '';

        tableState.isTableSelected = false;
        tableState.previewTableBlock = null;
        tableState.previewOriginalTableContent = '';

        formulaState.isFormulaSelected = false;
        formulaState.formulaInfo = null;
        formulaState.previewFormulaText = '';
        formulaState.previewOriginalFormulaText = '';

        chapterState.previewChapterContent = null;
        chapterState.previewOriginalContent = null;
        chapterState.previewChapterRange = null;

        resetGenerationState();
        setTimeout(() => {
            commonState.isResetting = false;
        }, 100);
    }, [
        editor,
        commonState,
        resetGenerationState,
        rewriteTask,
        chapterForm,
        chapterState,
        textState,
        imgState,
        tableState,
        formulaState,
    ]);

    const handleQuickAction = useCallback(
        (action) => {
            textState.currentAction = action;
            let instruction = '';
            switch (action) {
                case ACTION_TYPE.EXPAND:
                    instruction = '将内容扩写，增加细节、案例和论述，使其更加充分完整。';
                    break;
                case ACTION_TYPE.SHRINK:
                    instruction = '将内容缩写，保留核心要点，使其更简洁明了。';
                    break;
                case ACTION_TYPE.REDUCE_DUPLICATE:
                    instruction = '降重内容，改写表达方式，降低重复率。';
                    break;
                default:
                    break;
            }
            textState.customInstruction = instruction;
        },
        [textState],
    );

    const value = {
        editor,
        uiState,
        selectionState,
        documentState,
        scrollContainerRef,

        commonState,
        textState,
        chapterForm,
        tableState,
        imgState,
        formulaState,
        chapterState,
        rewriteTask,

        outline,
        chapterTreeData,
        rewritableChapters,

        calculateTargetLength,
        formatLengthDisplay,

        findImageNodeByBlockId,
        findTableNodeByBlockId,
        findFormulaNodeByBlockId,
        findHeadingNodeByBlockId,
        findChapterRange,
        scrollToChapter,

        saveBlockContent,
        handleCancelPreview,
        handleCopyResult,
        copyToClipboard,
        handleReplaceContent,
        handleReset,
        handleQuickAction,

        startChapterRewrite,
        updateChapterRewriteState,
        finishChapterRewrite,

        onApplyTextResult,
        modeDescriptions,
    };

    return <RightPanelContext.Provider value={value}>{children}</RightPanelContext.Provider>;
};

export default RightPanelContext;
